import React, { useState } from "react";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import SplitscreenOutlinedIcon from "@mui/icons-material/SplitscreenOutlined";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  IconButton,
  Typography,
  Popover,
  Paper,
  Badge,
  Tooltip,
  Fade,
  TextField
} from "@mui/material";
import clsx from "clsx";
import { makeStyles } from "@mui/styles";
import { useIntl } from "react-intl";
import { scrollPageIntoView } from "../../../../reader/pdf/utils";
import { VIEWS } from "../../consts";
import { useDispatch, useSelector } from "react-redux";
import { selectedQuestionHighlights } from "../../../../../redux/interactionsSlice";
import { setCurrentPage } from "../../../../../redux/pdfSlice";

const useStyles = makeStyles((theme) => ({
  actionBar: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: "0 0 0 16px",
    height: "48px",
    backgroundColor: theme.palette.grey.paper,
    zIndex: 1000
  },
  actionGroup: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center"
  },
  actionGroupCompact: {
    marginInlineEnd: "4px",
    "&:last-child": {
      marginBottom: 0
    }
  },
  readerActionButton: {
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: "4px",
    display: "flex",
    height: "36px",
    width: "36px",
    padding: "8px",
    alignItems: "center",
    "&:hover": {
      backgroundColor: theme.palette.action.hover || "rgba(0, 0, 0, 0.04)"
    }
  },
  leftButton: {
    borderRadius: "4px 0 0 4px",
    borderRight: "0"
  },
  rightButton: {
    borderRadius: "0 4px 4px 0"
  },
  selectedView: {
    backgroundColor: theme.palette.grey.selected
  },
  isCompatReaderActionButton: {
    height: "30px",
    width: "30px"
  },
  navigationContainer: {
    padding: theme.spacing(2),
    maxWidth: "300px"
  },
  navigationHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2)
  },
  navigationTitle: {
    fontWeight: 500
  },
  navigationControls: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: theme.spacing(2, 0)
  },
  navigationCounter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    marginInline: theme.spacing(1)
  },
  navigationButton: {
    border: `1px solid ${theme.palette.border.main}`,
    margin: theme.spacing(0, 0.5)
  },
  highlightPreview: {
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
    position: "relative"
  },
  highlightContent: {
    fontSize: "0.875rem",
    fontStyle: "italic",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  pageInfo: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1)
  },
  pageNavigationControls: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  pageCounter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    margin: "0 8px"
  },
  pageNumbering: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  totalPages: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem"
  },
  pageNavInput: {
    width: "24px",
    margin: "0 4px",
    padding: 0,
    "& input": {
      padding: "0",
      textAlign: "center"
    }
  },
  barIcon: {
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)"
    }
  },
  badge: {
    "& .MuiBadge-badge": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText
    }
  },
  popoverButtonContainer: {
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  popoverActionButton: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
    cursor: "pointer",
    width: "100%",
    justifyContent: "flex-start",
    "&:hover": {
      backgroundColor: theme.palette.grey[200]
    }
  },
  buttonIcon: {
    marginRight: theme.spacing(1)
  }
}));

const ReaderActions = ({
  activeMode,
  onModeChange,
  combinedViewMode,
  handleCombinedViewToggle // Add this prop to support combined view toggling
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();

  const questionHighlights = useSelector(selectedQuestionHighlights);

  // Popover state
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const popoverId = open ? "highlights-navigation-popover" : undefined;

  // View states
  const isReaderView = activeMode === VIEWS.READER;

  // PDF state
  const currentPage = useSelector((state) => state.pdf.currentPage) || 1;
  const totalPages = useSelector((state) => state.pdf.totalPages) || 1;

  // Event handlers
  const handlePopoverOpen = (event) => setAnchorEl(event.currentTarget);
  const handlePopoverClose = () => setAnchorEl(null);

  // Modified handleViewChange to also turn off combined view
  const handleViewChange = (view) => () => {
    // If we're in combined view, turn it off when changing views
    if (combinedViewMode) {
      handleCombinedViewToggle(false);
    }

    // Change to the requested view
    onModeChange(view);
  };

  return (
    <Box className={classes.actionBar}>
      {/* Highlight navigation button with badge showing count */}
      <Tooltip
        title={intl.formatMessage({
          id: "highlight.navigation",
          defaultMessage: "Highlight Navigation"
        })}
        placement="bottom">
        <IconButton
          aria-label="Navigate highlights"
          aria-describedby={popoverId}
          onClick={handlePopoverOpen}
          className={clsx(classes.readerActionButton)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Enhanced highlight navigation popover */}
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center"
        }}
        TransitionComponent={Fade}>
        <Paper className={classes.navigationContainer}>
          {/* Page Navigation Section */}
          <Box>
            {isReaderView && (
              <>
                {" "}
                <Typography variant="subtitle2" gutterBottom>
                  {intl.formatMessage({
                    id: "page.navigation",
                    defaultMessage: "Page Navigation"
                  })}
                </Typography>
                <Box className={classes.pageNavigationControls}>
                  <IconButton
                    size="small"
                    disabled={currentPage <= 1}
                    onClick={() => {
                      const options = {
                        behavior: "smooth",
                        block: "start"
                      };
                      scrollPageIntoView(currentPage - 1, options);
                    }}>
                    <KeyboardArrowLeftIcon fontSize="small" />
                  </IconButton>

                  <Box className={classes.pageCounter}>
                    <Typography
                      component="span"
                      className={classes.pageNumbering}>
                      <TextField
                        placeholder={"page"}
                        variant="standard"
                        className={clsx(classes.pageNavInput)}
                        id="pdf-current-page-nav-input"
                        aria-label="current-page-input"
                        type="number"
                        sx={{
                          "& .Mui-focused": {
                            outline: "2px solid white"
                          }
                        }}
                        value={currentPage}
                        onChange={(e) => {
                          const options = {
                            inline: "start",
                            block: "start"
                          };
                          const pageNumber = Number(e.target.value);
                          dispatch(setCurrentPage(pageNumber));
                          scrollPageIntoView(pageNumber, options);
                        }}
                        slotProps={{
                          input: { disableUnderline: true }
                        }}
                      />
                      <Typography
                        component="span"
                        className={classes.totalPages}>
                        {" / "}
                      </Typography>
                      <Typography
                        component="span"
                        className={classes.totalPages}>
                        {totalPages || " "}
                      </Typography>
                    </Typography>
                  </Box>

                  <IconButton
                    size="small"
                    disabled={currentPage >= totalPages}
                    onClick={() => {
                      const options = {
                        behavior: "smooth",
                        block: "start"
                      };
                      scrollPageIntoView(currentPage + 1, options);
                    }}>
                    <KeyboardArrowRightIcon fontSize="small" />
                  </IconButton>
                </Box>
              </>
            )}
            {/* View Toggle Section - conditionally shows the opposite view button */}
            <Box className={classes.navigationSection}>
              <Box className={classes.popoverButtonContainer}>
                {isReaderView ? (
                  // Show highlights button when in reader view
                  <Box
                    onClick={() => {
                      handleViewChange(VIEWS.HIGHLIGHTS)();
                      handlePopoverClose();
                    }}
                    className={classes.popoverActionButton}>
                    <SplitscreenOutlinedIcon
                      fontSize="small"
                      className={classes.buttonIcon}
                    />
                    <Typography variant="body2">
                      {intl.formatMessage({
                        id: "show.highlights.cards",
                        defaultMessage: "Card view"
                      })}
                    </Typography>
                    {questionHighlights.length > 0 && (
                      <Badge
                        badgeContent={questionHighlights.length}
                        className={classes.badge}
                        max={99}
                        sx={{ marginLeft: "auto" }}
                      />
                    )}
                  </Box>
                ) : (
                  // Show text button when in highlights view
                  <Box
                    onClick={() => {
                      handleViewChange(VIEWS.READER)();
                      handlePopoverClose();
                    }}
                    className={classes.popoverActionButton}>
                    <ArticleOutlinedIcon
                      fontSize="small"
                      className={classes.buttonIcon}
                    />
                    <Typography variant="body2">
                      {intl.formatMessage({
                        id: "show.text",
                        defaultMessage: "Text view"
                      })}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>
      </Popover>
    </Box>
  );
};

export default ReaderActions;
